import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Clock, Save, Undo2, Zap } from 'lucide-react';
import { adminSession } from '@/api/admin';
import { SessionApplyNowDialog } from '@/features/session/SessionApplyNowDialog';
import type { SessionWindowIssue, SessionWindowValidation, TradingRange, TradingScheduleDay, TradingSession, WeekDay } from '@/api/types';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Pill } from '@/components/widgets/Pill';
import { ApiError } from '@/lib/http';
import { toast } from '@/components/ui/sonner';
import { cn } from '@/lib/utils';
import { fmtDateTime } from '@/lib/format';

const ALL_DAYS: WeekDay[] = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

export function SessionConfig() {
  const qc = useQueryClient();
  const sessQ = useQuery({ queryKey: ['admin', 'trading-session'], queryFn: adminSession.get });

  const [draft, setDraft] = useState<TradingSession | null>(null);
  const [note, setNote] = useState('');
  const [revokeOpen, setRevokeOpen] = useState(false);
  const [applyOpen, setApplyOpen] = useState(false);

  useEffect(() => {
    if (sessQ.data) setDraft(sessQ.data);
  }, [sessQ.data]);

  const revoke = useMutation({
    mutationFn: () => adminSession.revokePending(),
    onSuccess: () => {
      toast.success('已撤销待生效变更', { description: '时段配置的待生效改动已清除' });
      setRevokeOpen(false);
      qc.invalidateQueries({ queryKey: ['admin', 'trading-session'] });
    },
    onError: (err) => toast.error('撤销失败', { description: err instanceof ApiError ? err.message : '请稍后重试' }),
  });

  const dirty = useMemo(() => {
    if (!draft || !sessQ.data) return false;
    const a = draft;
    const b = sessQ.data;
    const m = (v?: number) => v ?? 0;
    return (
      a.openHour !== b.openHour ||
      m(a.openMinute) !== m(b.openMinute) ||
      a.windDownHour !== b.windDownHour ||
      m(a.windDownMinute) !== m(b.windDownMinute) ||
      a.liquidationHour !== b.liquidationHour ||
      m(a.liquidationMinute) !== m(b.liquidationMinute) ||
      a.forceCloseHour !== b.forceCloseHour ||
      m(a.forceCloseMinute) !== m(b.forceCloseMinute) ||
      a.tradingDays.length !== b.tradingDays.length ||
      a.tradingDays.some((d, i) => d !== b.tradingDays[i])
    );
  }, [draft, sessQ.data]);

  const submit = useMutation({
    mutationFn: () => {
      if (!draft) throw new Error('no draft');
      return adminSession.update({
        openHour: draft.openHour,
        openMinute: draft.openMinute ?? 0,
        windDownHour: draft.windDownHour,
        windDownMinute: draft.windDownMinute ?? 0,
        liquidationHour: draft.liquidationHour,
        liquidationMinute: draft.liquidationMinute ?? 0,
        forceCloseHour: draft.forceCloseHour,
        forceCloseMinute: draft.forceCloseMinute ?? 0,
        tradingDays: draft.tradingDays,
        note: note.trim() || undefined,
      });
    },
    onSuccess: (res) => {
      const warns = res?.validation?.issues ?? [];
      if (warns.length) {
        toast.warning('已保存(有提示)', {
          description: `将于次日 03:05 生效。${warns.map((i) => i.message).join('；')}`,
        });
      } else {
        toast.success('已保存', { description: '将于次日 03:05(交易窗口前)生效' });
      }
      qc.invalidateQueries({ queryKey: ['admin', 'trading-session'] });
      setNote('');
    },
    onError: (err) => {
      // 后端校验拦截:400 details 携带 { ok, issues }(配置落在 cTrader 休市窗口)
      const issues =
        err instanceof ApiError && Array.isArray((err.details as { issues?: SessionWindowIssue[] })?.issues)
          ? ((err.details as { issues: SessionWindowIssue[] }).issues || []).filter((i) => i.severity === 'error')
          : [];
      if (issues.length) {
        toast.error('保存被拦截:配置落在 cTrader 休市窗口', {
          description: issues.map((i) => i.message).join('；'),
        });
      } else {
        toast.error('保存失败', { description: err instanceof ApiError ? err.message : '' });
      }
    },
  });

  if (!draft) return null;

  const toggleDay = (d: WeekDay) => {
    const has = draft.tradingDays.includes(d);
    setDraft({
      ...draft,
      tradingDays: has ? draft.tradingDays.filter((x) => x !== d) : [...draft.tradingDays, d].sort(orderDays),
    });
  };

  return (
    <div className="p-6 space-y-4">
      <header>
        <h1 className="text-xl font-bold text-ink-0 flex items-center gap-2">
          <Clock className="h-5 w-5 text-brand" />
          交易时段配置
        </h1>
        <p className="text-sm text-ink-2 mt-1">
          时区 / 开仓 / 收尾 / 强平 / 兜底(均精确到分钟)/ 交易日 · 修改默认次日 03:05 生效(§9.10)
        </p>
      </header>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>当前生效配置</span>
            {sessQ.data?.pendingChange && (
              <Pill tone="warn">
                有待生效变更
                {sessQ.data.pendingChange.effectiveAt
                  ? ` · ${fmtDateTime(sessQ.data.pendingChange.effectiveAt)}`
                  : ''}
              </Pill>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-5">
          {sessQ.data?.pendingChange && (
            <div className="rounded-md border border-warn/40 bg-warn/10 px-4 py-3 space-y-1.5">
              <div className="flex items-center justify-between gap-2">
                <div className="text-sm font-semibold text-warn-dim">有待生效变更</div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="warn"
                    size="sm"
                    onClick={() => setApplyOpen(true)}
                    disabled={revoke.isPending}
                  >
                    <Zap className="h-3.5 w-3.5" />
                    立即生效
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setRevokeOpen(true)}
                    disabled={revoke.isPending}
                  >
                    <Undo2 className="h-3.5 w-3.5" />
                    撤销待生效
                  </Button>
                </div>
              </div>
              <PendingSessionDiff current={sessQ.data} pending={sessQ.data.pendingChange} />
              {sessQ.data.pendingChange.note && (
                <div className="text-xs text-ink-2">备注:{sessQ.data.pendingChange.note}</div>
              )}
            </div>
          )}

          <Row>
            <Label className="w-32 shrink-0">时区</Label>
            <span className="num font-medium">{draft.timezone}</span>
            <span className="text-xs text-ink-3">(目前不允许通过 UI 修改,如需调整请联系运维)</span>
          </Row>

          {/* 四边界均精确到分钟。顺序约束(后端按分钟序强校验):收尾 < 强平 < 兜底 < 开仓。 */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <TimeField
              label="开仓时刻"
              hour={draft.openHour}
              minute={draft.openMinute ?? 0}
              onChange={(h, m) => setDraft({ ...draft, openHour: h, openMinute: m })}
            />
            <TimeField
              label="收尾开始"
              hour={draft.windDownHour}
              minute={draft.windDownMinute ?? 0}
              onChange={(h, m) => setDraft({ ...draft, windDownHour: h, windDownMinute: m })}
            />
            <TimeField
              label="强平时刻"
              hour={draft.liquidationHour}
              minute={draft.liquidationMinute ?? 0}
              onChange={(h, m) => setDraft({ ...draft, liquidationHour: h, liquidationMinute: m })}
              hint="自动平仓窗口起点,须早于兜底"
            />
            <TimeField
              label="强平兜底"
              hour={draft.forceCloseHour}
              minute={draft.forceCloseMinute ?? 0}
              onChange={(h, m) => setDraft({ ...draft, forceCloseHour: h, forceCloseMinute: m })}
              hint="静默期起点,贴近 cTrader 收盘"
            />
          </div>

          <div className="space-y-2">
            <Label>交易日</Label>
            <div className="flex flex-wrap gap-2">
              {ALL_DAYS.map((d) => {
                const active = draft.tradingDays.includes(d);
                return (
                  <button
                    key={d}
                    type="button"
                    onClick={() => toggleDay(d)}
                    className={cn(
                      'rounded-md border px-3 py-1.5 text-sm transition-colors btn-press',
                      active ? 'border-brand bg-brand/10 text-brand-dim font-medium' : 'border-line bg-bg-2 text-ink-2 hover:bg-bg-3',
                    )}
                  >
                    {d}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="sess-note">备注(写入 audit_logs)</Label>
            <Input id="sess-note" value={note} onChange={(e) => setNote(e.target.value)} placeholder="变更原因 / 工单号" disabled={submit.isPending} />
          </div>

          <div className="flex items-center justify-between pt-2 border-t border-line">
            <div className="text-xs text-ink-3 num">
              当前: {fmtHM(draft.openHour, draft.openMinute)} 开仓 → {fmtHM(draft.windDownHour, draft.windDownMinute)} 收尾
              → {fmtHM(draft.liquidationHour, draft.liquidationMinute)} 强平 → {fmtHM(draft.forceCloseHour, draft.forceCloseMinute)} 兜底
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" disabled={!dirty || submit.isPending} onClick={() => sessQ.data && setDraft(sessQ.data)}>
                重置
              </Button>
              <Button onClick={() => submit.mutate()} disabled={!dirty || submit.isPending}>
                <Save className="h-4 w-4" />
                {submit.isPending ? '保存中...' : '保存待生效'}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* ── cTrader 官方时段对照 + 当前配置校验 ──
          校验徽标(validation)是后端基于「已保存」配置算出的;draft 改动未保存前其结果已过期,
          故传入 dirty:有未保存改动时灰显并提示「保存后重新校验」,避免按旧值误导。 */}
      <MarketReferenceCard session={sessQ.data} dirty={dirty} />

      {/* ── 撤销待生效二次确认 Dialog ── */}
      <Dialog open={revokeOpen} onOpenChange={(v) => !v && !revoke.isPending && setRevokeOpen(false)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-warn">
              <Undo2 className="h-5 w-5" />
              撤销待生效变更
            </DialogTitle>
            <DialogDescription>
              将清除时段配置当前所有<strong>待生效(次日 03:05 生效)</strong>的改动,恢复为当前生效配置。此操作不影响已生效的配置。
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRevokeOpen(false)} disabled={revoke.isPending}>
              取消
            </Button>
            <Button variant="warn" onClick={() => revoke.mutate()} disabled={revoke.isPending}>
              {revoke.isPending ? '撤销中…' : '确认撤销'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <SessionApplyNowDialog open={applyOpen} onOpenChange={setApplyOpen} />
    </div>
  );
}

/**
 * 时段待生效 diff(#22,只读展示)。
 * 后端 pendingChange 含各 session 短键(openHour/windDownHour/...)的待生效值,
 * 这里逐字段对比「当前生效值 → 待生效值」,只渲染真正发生变化的字段。
 * 后端只回填变更过的字段,故各字段可选(!= null 才参与对比)。
 */
function PendingSessionDiff({
  current,
  pending,
}: {
  current: TradingSession;
  pending: NonNullable<TradingSession['pendingChange']>;
}) {
  const rows: Array<[string, string, string]> = [];
  const m = (v?: number) => v ?? 0;
  // 每个边界 = 小时:分钟;pending 只回填变更过的字段(小时/分钟任一可单独变),按 HH:MM 合并对比。
  const pushTime = (
    label: string,
    curH: number | undefined,
    curM: number | undefined,
    nextH: number | undefined,
    nextM: number | undefined,
  ) => {
    if ((nextH == null && nextM == null) || curH == null) return;
    const effH = nextH ?? curH;
    const effM = nextM ?? m(curM);
    if (effH === curH && effM === m(curM)) return;
    rows.push([label, fmtHM(curH, curM), fmtHM(effH, effM)]);
  };
  pushTime('开仓时刻', current.openHour, current.openMinute, pending.openHour, pending.openMinute);
  pushTime('收尾开始', current.windDownHour, current.windDownMinute, pending.windDownHour, pending.windDownMinute);
  pushTime('强平时刻', current.liquidationHour, current.liquidationMinute, pending.liquidationHour, pending.liquidationMinute);
  pushTime('强平兜底', current.forceCloseHour, current.forceCloseMinute, pending.forceCloseHour, pending.forceCloseMinute);

  const curDays = current.tradingDays ?? [];
  const nextDays = pending.tradingDays;
  if (
    nextDays != null &&
    (nextDays.length !== curDays.length || nextDays.some((d, i) => d !== curDays[i]))
  ) {
    rows.push(['交易日', curDays.join(' '), nextDays.join(' ')]);
  }

  if (rows.length === 0) return <div className="text-xs text-warn-dim">(暂无字段差异显示)</div>;
  return (
    <div className="space-y-0.5 text-xs num text-warn-dim">
      {rows.map(([label, before, after]) => (
        <div key={label}>
          {label}:<span className="text-ink-2 line-through ml-1">{before}</span>
          <span className="mx-1.5 text-ink-3">→</span>
          <span className="text-warn font-semibold">{after}</span>
        </div>
      ))}
    </div>
  );
}

// ── cTrader 时段对照:连续跨日会话重建 + 倒计时 ────────────────────────

interface ContSession {
  startWeekday: number;
  startTime: string;
  endWeekday: number;
  endTime: string;
}

const WDAY_LABELS = ['', '周一', '周二', '周三', '周四', '周五', '周六', '周日'];

/** 把后端按日历日切分的 schedule 重组为连续跨日交易时段(与 cTrader 客户端对齐)。 */
function buildContSessions(schedule: TradingScheduleDay[]): ContSession[] {
  const dayMap = new Map<number, TradingRange[]>();
  for (const d of schedule) dayMap.set(d.weekday, d.ranges);

  const sessions: ContSession[] = [];
  let cur: ContSession | null = null;

  for (let wd = 1; wd <= 7; wd++) {
    for (const rng of dayMap.get(wd) ?? []) {
      if (rng.open === '00:00' && cur !== null) {
        // 本段是昨夜盘的延续
        cur.endWeekday = wd;
        cur.endTime = rng.close;
        if (rng.close !== '24:00') {
          sessions.push(cur);
          cur = null;
        }
      } else {
        // 新时段开始
        if (cur !== null) sessions.push(cur);
        cur = { startWeekday: wd, startTime: rng.open, endWeekday: wd, endTime: rng.close };
        if (rng.close !== '24:00') {
          sessions.push(cur);
          cur = null;
        }
      }
    }
  }
  if (cur !== null) sessions.push(cur);
  return sessions;
}

/** 把 UTC ISO 字符串解析为给定 IANA 时区的 { weekday(1-7), time("HH:MM") }。 */
function parseToWeekdayTime(isoUtc: string, tz: string): { weekday: number; time: string } {
  const d = new Date(isoUtc);
  const wdStr = new Intl.DateTimeFormat('en-US', { weekday: 'long', timeZone: tz }).format(d);
  const rawTime = new Intl.DateTimeFormat('en-US', {
    hour: '2-digit', minute: '2-digit', hour12: false, timeZone: tz,
  }).format(d);
  const wdMap: Record<string, number> = {
    Monday: 1, Tuesday: 2, Wednesday: 3, Thursday: 4,
    Friday: 5, Saturday: 6, Sunday: 7,
  };
  // 修正格式：某些 Intl 实现会输出 "4:59"/"24:00"
  const time = rawTime.replace(/^24:/, '00:').replace(/^(\d):/, '0$1:');
  return { weekday: wdMap[wdStr] ?? 0, time };
}

/** 实时倒计时 hook，每秒更新，返回 "HH:MM:SS"。 */
function useCountdown(targetIso: string | null | undefined): string {
  const [display, setDisplay] = useState('');
  useEffect(() => {
    if (!targetIso) { setDisplay(''); return; }
    const update = () => {
      const diff = new Date(targetIso).getTime() - Date.now();
      if (diff <= 0) { setDisplay('00:00:00'); return; }
      const h = Math.floor(diff / 3_600_000);
      const m = Math.floor((diff % 3_600_000) / 60_000);
      const s = Math.floor((diff % 60_000) / 1_000);
      setDisplay(
        `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`,
      );
    };
    update();
    const id = setInterval(update, 1_000);
    return () => clearInterval(id);
  }, [targetIso]);
  return display;
}

/** 单个连续时段行，带可选倒计时徽标。 */
function ContSessionRow({
  session,
  closeAt,
  openAt,
  isActive,
}: {
  session: ContSession;
  closeAt?: string | null;
  openAt?: string | null;
  isActive: boolean;
}) {
  const closeCountdown = useCountdown(closeAt);
  const openCountdown = useCountdown(openAt);

  const endLabel =
    session.startWeekday !== session.endWeekday ? `${WDAY_LABELS[session.endWeekday]} ` : '';

  return (
    <div className={cn('flex justify-between items-center gap-4 text-sm', isActive && 'font-medium')}>
      <span className={cn('w-12 shrink-0', isActive ? 'text-up' : 'text-ink-2')}>
        {WDAY_LABELS[session.startWeekday]}
      </span>
      <span className="num flex-1 text-right flex items-center justify-end gap-2">
        <span className={isActive ? 'text-up' : 'text-ink-1'}>
          {session.startTime} - {endLabel}{session.endTime}
        </span>
        {closeCountdown && (
          <span className="text-xs text-up-dim">({closeCountdown} 休市)</span>
        )}
        {openCountdown && (
          <span className="text-xs text-ink-3">({openCountdown} 开市)</span>
        )}
      </span>
    </div>
  );
}

/**
 * cTrader 官方交易时段对照(已换算为北京时间,与 cTrader 客户端一致)+ 当前生效配置校验。
 * 供后台对照设定:平台「开仓→兜底」窗口应落在 cTrader 官方窗口内,保存时后端会强校验。
 */
export function MarketReferenceCard({
  session,
  dirty = false,
}: {
  session?: TradingSession;
  /** 上层 draft 是否有未保存改动:为 true 时 validation 徽标已过期,改为「保存后校验」提示。 */
  dirty?: boolean;
}) {
  const refs = session?.marketReference ?? [];
  const validation = session?.validation;
  if (refs.length === 0 && !validation) return null;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Clock className="h-5 w-5 text-brand" />
          cTrader 官方交易时段对照(北京时间)
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {validation &&
          (dirty ? (
            <div className="rounded-md border border-line bg-bg-3 px-3 py-2 text-sm text-ink-3">
              有未保存改动,下方校验结果按<strong className="text-ink-2">已保存</strong>配置显示;保存后将按新配置重新校验。
            </div>
          ) : (
            <ValidationBanner v={validation} />
          ))}

        {refs.map((r) => {
          const sessions = buildContSessions(r.schedule);
          const closePt = r.nextCloseAt ? parseToWeekdayTime(r.nextCloseAt, r.timezone) : null;
          const openPt = r.nextOpenAt ? parseToWeekdayTime(r.nextOpenAt, r.timezone) : null;

          return (
            <div key={r.symbolId} className="space-y-1">
              <div className="flex items-center justify-between text-sm">
                <span className="font-medium text-ink-1">{r.symbolName}</span>
                <span className="text-xs text-ink-3 num">
                  {r.timezone}
                  {r.sourceTimeZone ? ` · 源 ${r.sourceTimeZone}` : ''}
                </span>
              </div>
              {sessions.map((s) => {
                const isActive =
                  !!r.marketOpen &&
                  closePt !== null &&
                  s.endWeekday === closePt.weekday &&
                  s.endTime === closePt.time;
                const isNext =
                  !r.marketOpen &&
                  openPt !== null &&
                  s.startWeekday === openPt.weekday &&
                  s.startTime === openPt.time;
                return (
                  <ContSessionRow
                    key={`${s.startWeekday}-${s.startTime}`}
                    session={s}
                    closeAt={isActive ? r.nextCloseAt : null}
                    openAt={isNext ? r.nextOpenAt : null}
                    isActive={isActive}
                  />
                );
              })}
              {r.syncedAt && (
                <div className="text-xs text-ink-3">官方时段 · 上次同步 {fmtDateTime(r.syncedAt)}</div>
              )}
            </div>
          );
        })}

        <div className="text-xs text-ink-3 leading-relaxed pt-2 border-t border-line">
          以上为 cTrader 官方交易时间(已换算为北京时间,与 cTrader 客户端显示一致)。请确保上方
          「开仓 → 兜底」窗口落在此范围内;<strong className="text-ink-2">保存时系统会自动校验</strong>,
          若强平/兜底落在 cTrader 休市(无法平仓)将拦截保存。
        </div>
      </CardContent>
    </Card>
  );
}

export function ValidationBanner({ v }: { v: SessionWindowValidation }) {
  const errors = v.issues.filter((i) => i.severity === 'error');
  const warns = v.issues.filter((i) => i.severity === 'warn');
  if (v.issues.length === 0) {
    return (
      <div className="rounded-md border border-up/40 bg-up/10 px-3 py-2 text-sm text-up-dim">
        ✓ 当前配置落在 cTrader 官方交易窗口内
      </div>
    );
  }
  return (
    <div
      className={cn(
        'rounded-md border px-3 py-2 text-sm space-y-1',
        errors.length ? 'border-down/40 bg-down/10' : 'border-warn/40 bg-warn/10',
      )}
    >
      <div className={cn('font-semibold', errors.length ? 'text-down' : 'text-warn-dim')}>
        {errors.length ? `发现 ${errors.length} 项冲突(需修正)` : `${warns.length} 项提示`}
      </div>
      {v.issues.map((i, idx) => (
        <div key={idx} className={cn('text-xs', i.severity === 'error' ? 'text-down' : 'text-warn-dim')}>
          · {i.message}
        </div>
      ))}
    </div>
  );
}

function Row({ children }: { children: React.ReactNode }) {
  return <div className="flex items-center gap-3 flex-wrap">{children}</div>;
}

/** 格式化为 `HH:MM`(分钟缺省视为 0)。供时段展示 / 待生效 diff 统一口径。 */
export function fmtHM(hour: number, minute?: number): string {
  return `${String(hour).padStart(2, '0')}:${String(minute ?? 0).padStart(2, '0')}`;
}

/**
 * 时:分输入(小时 0-23 + 分钟 0-59),用于开仓 / 收尾 / 强平 / 兜底四个边界。
 * onChange 同时回传 (hour, minute);任一框失焦/改动都会带上另一框的当前值。
 * 交易时段组页(TradingGroups)复用同一控件,保证两处四边界表单形状一致。
 */
export function TimeField({
  label,
  hour,
  minute,
  onChange,
  hint,
}: {
  label: string;
  hour: number;
  minute: number;
  onChange: (hour: number, minute: number) => void;
  hint?: string;
}) {
  return (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      <div className="flex items-center gap-1.5">
        <Input
          type="number"
          min={0}
          max={23}
          value={hour}
          aria-label={`${label}小时`}
          onChange={(e) => onChange(Math.max(0, Math.min(23, Number(e.target.value) || 0)), minute)}
          className="num w-16 text-center"
        />
        <span className="text-ink-3 num">:</span>
        <Input
          type="number"
          min={0}
          max={59}
          value={minute}
          aria-label={`${label}分钟`}
          onChange={(e) => onChange(hour, Math.max(0, Math.min(59, Number(e.target.value) || 0)))}
          className="num w-16 text-center"
        />
      </div>
      {hint && <p className="text-xs text-ink-3 leading-snug">{hint}</p>}
    </div>
  );
}

const DAY_ORDER: Record<WeekDay, number> = { Mon: 0, Tue: 1, Wed: 2, Thu: 3, Fri: 4, Sat: 5, Sun: 6 };
const orderDays = (a: WeekDay, b: WeekDay) => DAY_ORDER[a] - DAY_ORDER[b];
